import { useState, useEffect, useRef, useCallback } from 'react';
import '../assets/css/--component-chat-widget.css';
import '../assets/css/--component-chat-widget-embed.css';

// ============================================
// VOICE CHAT CONSTANTS
// ============================================
const RECORDING_TIMESLICE = 100; // ms
const VOICE_SAMPLE_RATE = 8000;
const VOICE_BUFFER_SIZE = 4096;
const USER_AUDIO_FORMAT = 'ulaw';
const AGENT_AUDIO_FORMAT = 'ulaw';
const HAPTIC_START = 50; // ms
const HAPTIC_STOP = 100; // ms
const AMPLITUDE_BARS_COUNT = 20;
const AMPLITUDE_MAX_HEIGHT = 22;
const AMPLITUDE_MIN_HEIGHT = 3;
const AMPLITUDE_SCALE = 60;
const BARGE_IN_RMS_THRESHOLD = 0.075;
const BARGE_IN_CONSECUTIVE_FRAMES = 4;
const BARGE_IN_COOLDOWN_MS = 1200;
const BARGE_IN_PLAYBACK_GRACE_MS = 450;

// Utility to convert audio Blob to Base64
const blobToBase64ArrayBuffer = async (blob) => {
    const arrayBuffer = await blob.arrayBuffer();
    return btoa(
        new Uint8Array(arrayBuffer)
            .reduce((data, byte) => data + String.fromCharCode(byte), '')
    );
};

// ============================================
// VOICE CHAT UTILITIES (ported from ChatComponent.js)
// ============================================
const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return mins > 0
        ? `${mins}:${secs.toString().padStart(2, '0')}`
        : `${secs}s`;
};

const generateSessionId = () =>
    `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

const triggerHaptic = (duration) => {
    if (navigator.vibrate) {
        navigator.vibrate(duration);
    }
};

const base64ToUint8Array = (base64) => {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);

    for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
    }

    return bytes;
};

const muLawToLinearSample = (muLawByte) => {
    const BIAS = 0x84;
    const byte = ~muLawByte & 0xff;
    const sign = byte & 0x80;
    const exponent = (byte >> 4) & 0x07;
    const mantissa = byte & 0x0f;
    let sample = ((mantissa << 3) + BIAS) << exponent;
    sample -= BIAS;

    return sign ? -sample : sample;
};

const decodeAgentAudio = (base64Audio, format = AGENT_AUDIO_FORMAT) => {
    const bytes = base64ToUint8Array(base64Audio);

    if (format === 'pcm16') {
        const sampleCount = Math.floor(bytes.length / 2);
        const samples = new Float32Array(sampleCount);
        const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);

        for (let i = 0; i < sampleCount; i++) {
            samples[i] = view.getInt16(i * 2, true) / 32768;
        }

        return samples;
    }

    const samples = new Float32Array(bytes.length);
    for (let i = 0; i < bytes.length; i++) {
        samples[i] = muLawToLinearSample(bytes[i]) / 32768;
    }

    return samples;
};

const downsampleBuffer = (buffer, inputSampleRate, outputSampleRate) => {
    if (outputSampleRate === inputSampleRate) {
        return buffer;
    }

    const sampleRateRatio = inputSampleRate / outputSampleRate;
    const newLength = Math.round(buffer.length / sampleRateRatio);
    const result = new Float32Array(newLength);
    let offsetResult = 0;
    let offsetBuffer = 0;

    while (offsetResult < result.length) {
        const nextOffsetBuffer = Math.round((offsetResult + 1) * sampleRateRatio);
        let accum = 0;
        let count = 0;

        for (let i = offsetBuffer; i < nextOffsetBuffer && i < buffer.length; i++) {
            accum += buffer[i];
            count++;
        }

        result[offsetResult] = count ? accum / count : 0;
        offsetResult++;
        offsetBuffer = nextOffsetBuffer;
    }

    return result;
};

const floatTo16BitPcm = (floatSamples) => {
    const buffer = new ArrayBuffer(floatSamples.length * 2);
    const view = new DataView(buffer);

    for (let i = 0; i < floatSamples.length; i++) {
        const sample = Math.max(-1, Math.min(1, floatSamples[i]));
        view.setInt16(i * 2, sample < 0 ? sample * 0x8000 : sample * 0x7fff, true);
    }

    return buffer;
};

const linearSampleToMuLaw = (sample) => {
    const BIAS = 0x84;
    const CLIP = 32635;
    let pcm = Math.max(-1, Math.min(1, sample));
    pcm = pcm < 0 ? pcm * 0x8000 : pcm * 0x7fff;

    let sign = 0;
    if (pcm < 0) {
        sign = 0x80;
        pcm = -pcm;
    }

    pcm = Math.min(CLIP, pcm) + BIAS;

    let exponent = 7;
    for (let mask = 0x4000; (pcm & mask) === 0 && exponent > 0; mask >>= 1) {
        exponent--;
    }

    const mantissa = (pcm >> (exponent + 3)) & 0x0f;
    return ~(sign | (exponent << 4) | mantissa) & 0xff;
};

const floatToMuLaw = (floatSamples) => {
    const bytes = new Uint8Array(floatSamples.length);

    for (let i = 0; i < floatSamples.length; i++) {
        bytes[i] = linearSampleToMuLaw(floatSamples[i]);
    }

    return bytes.buffer;
};

const encodeUserAudio = (floatSamples, format = USER_AUDIO_FORMAT) => {
    return format === 'pcm16'
        ? floatTo16BitPcm(floatSamples)
        : floatToMuLaw(floatSamples);
};

const normalizeTranscript = (text) =>
    (text || '')
        .toLowerCase()
        .replace(/[^a-z0-9\s]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();

const stripEchoedBotText = (transcript, botText) => {
    const normalizedTranscript = normalizeTranscript(transcript);
    const normalizedBotText = normalizeTranscript(botText);

    if (!normalizedTranscript || !normalizedBotText || normalizedBotText.length < 24) {
        return transcript;
    }

    if (normalizedTranscript === normalizedBotText) {
        return '';
    }

    if (normalizedTranscript.startsWith(normalizedBotText)) {
        return transcript.slice(botText.length).trim();
    }

    if (normalizedBotText.includes(normalizedTranscript) && normalizedTranscript.length > 24) {
        return '';
    }

    return transcript;
};

const getRmsLevel = (samples) => {
    if (!samples?.length) {
        return 0;
    }

    let sum = 0;
    for (let i = 0; i < samples.length; i++) {
        sum += samples[i] * samples[i];
    }

    return Math.sqrt(sum / samples.length);
};

export default function ChatWidget({ agentId, userId, wsUrl, backgroundColor = '', textColor = '', headerColor = '', buttonColor = '', borderRadius = '', chatTextColor = '' }) {
    const [messages, setMessages] = useState([]);
    const [typing, setTyping] = useState(false);
    const [input, setInput] = useState('');
    const [isRecording, setIsRecording] = useState(false);
    const [isVoiceChatActive, setIsVoiceChatActive] = useState(false);
    const [isConnected, setIsConnected] = useState(false);
    const [connectionError, setConnectionError] = useState(null);
    const [recordingDuration, setRecordingDuration] = useState(0);
    const [recordingAmplitude, setRecordingAmplitude] = useState(0);
    const messagesEndRef = useRef(null);
    const wsRef = useRef(null);
    const streamSid = useRef('chat-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9));
    const sessionId = useRef(generateSessionId());
    const mediaRecorder = useRef(null);
    const audioChunks = useRef([]);
    const chatStarted = useRef(false);
    const messagesContainerRef = useRef(null);
    const isConnecting = useRef(false);
    const inputModeRef = useRef('text');

    // Live voice chat refs (ported from ChatComponent.js)
    const audioContextRef = useRef(null);
    const playbackAudioContextRef = useRef(null);
    const playbackTimeRef = useRef(0);
    const playbackSourcesRef = useRef(new Set());
    const agentSpeakingUntilRef = useRef(0);
    const agentSpeakingStartedAtRef = useRef(0);
    const bargeInVoiceFramesRef = useRef(0);
    const lastBargeInAtRef = useRef(0);
    const ignoreAgentAudioUntilRef = useRef(0);
    const lastBotMessageRef = useRef('');
    const analyserRef = useRef(null);
    const animationFrameRef = useRef(null);
    const streamRef = useRef(null);
    const mediaStreamSourceRef = useRef(null);
    const processorRef = useRef(null);
    const isRecordingRef = useRef(false);
    const isVoiceChatActiveRef = useRef(false);
    const voiceSessionReadyRef = useRef(false);
    const voiceChunkCountRef = useRef(0);
    const activeTranscriptMessageIdRef = useRef(null);
    const recordingStartTimeRef = useRef(null);
    const recordingTimerRef = useRef(null);

    // Create a unique storage key for this user/agent combination
    const STORAGE_KEY = `chat_messages_${agentId}_${userId}`;

    // Load messages from localStorage on component mount
    useEffect(() => {
        const savedMessages = localStorage.getItem(STORAGE_KEY);
        if (savedMessages) {
            try {
                const parsedMessages = JSON.parse(savedMessages);
                setMessages(parsedMessages);
                console.log('Loaded saved messages from localStorage:', parsedMessages.length);
            } catch (error) {
                console.error('Error loading messages from localStorage:', error);
            }
        }
    }, [agentId, userId]);

    // Save messages to localStorage whenever they change
    useEffect(() => {
        if (messages.length > 0) {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(messages));
            console.log('Saved messages to localStorage:', messages.length);
        }
    }, [messages]);

    // Auto-scroll to bottom
    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages, typing]);

    // Append message to UI
    const appendMessageToUI = (sender, text) => {
        if (!text) return; // Guard against empty messages

        setMessages(prev => [...prev, {
            sender,
            text,
            timestamp: new Date().toLocaleTimeString()
        }]);
    };

    // Live-update a single "in progress" voice transcript message (ported from ChatComponent.js)
    const upsertVoiceTranscript = useCallback((text, isFinal = false) => {
        const trimmed = text?.trim();
        if (!trimmed) return;

        setMessages((prev) => {
            const timestamp = new Date().toLocaleTimeString();
            const activeId = activeTranscriptMessageIdRef.current;

            if (activeId) {
                const existingIndex = prev.findIndex((msg) => msg.id === activeId);
                if (existingIndex !== -1) {
                    const updated = [...prev];
                    updated[existingIndex] = {
                        ...updated[existingIndex],
                        text: trimmed,
                        timestamp,
                    };

                    if (isFinal) {
                        activeTranscriptMessageIdRef.current = null;
                    }

                    return updated;
                }
            }

            const id = `voice-transcript-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
            activeTranscriptMessageIdRef.current = isFinal ? null : id;

            return [
                ...prev,
                {
                    id,
                    sender: 'user',
                    text: trimmed,
                    timestamp,
                },
            ];
        });
    }, []);

    // Send text message
    const sendMessage = (messageText) => {
        if (!messageText.trim() || !wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;

        wsRef.current.send(JSON.stringify({
            event: 'chat_message',
            streamSid: streamSid.current,
            sessionId: sessionId.current,
            inputMode: 'text',
            message: messageText
        }));

        appendMessageToUI('user', messageText);
        setInput('');
    };

    // Send audio message
    const sendAudio = async (blob) => {
        if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;

        try {
            const base64Audio = await blobToBase64ArrayBuffer(blob);

            wsRef.current.send(JSON.stringify({
                event: 'chat_message',
                streamSid: streamSid.current,
                message: '[AUDIO_MESSAGE]',
                audio: base64Audio
            }));
        } catch (error) {
            console.error('Error sending audio:', error);
        }
    };

    // Binary send helper for live voice audio frames
    const safeSendBinary = useCallback((buffer) => {
        if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
            return false;
        }

        try {
            wsRef.current.send(buffer);
            return true;
        } catch (error) {
            console.error('[Voice] Binary send error:', error);
            return false;
        }
    }, []);

    const stopAgentPlayback = useCallback(async () => {
        playbackSourcesRef.current.forEach((source) => {
            try {
                source.stop();
            } catch (error) {
                // Ignore sources that have already ended.
            }
        });
        playbackSourcesRef.current.clear();
        agentSpeakingUntilRef.current = 0;
        agentSpeakingStartedAtRef.current = 0;
        bargeInVoiceFramesRef.current = 0;
        playbackTimeRef.current = 0;

        if (playbackAudioContextRef.current) {
            try {
                await playbackAudioContextRef.current.close();
            } catch (error) {
                console.warn('[Voice] Playback context close error:', error);
            }
            playbackAudioContextRef.current = null;
        }
    }, []);

    const playAgentAudio = useCallback((base64Audio) => {
        try {
            const AudioContextClass = window.AudioContext || window.webkitAudioContext;
            if (!playbackAudioContextRef.current) {
                playbackAudioContextRef.current = new AudioContextClass({ sampleRate: VOICE_SAMPLE_RATE });
                playbackTimeRef.current = playbackAudioContextRef.current.currentTime;
            }

            const audioContext = playbackAudioContextRef.current;
            const samples = decodeAgentAudio(base64Audio);
            const audioBuffer = audioContext.createBuffer(1, samples.length, VOICE_SAMPLE_RATE);
            audioBuffer.copyToChannel(samples, 0);

            const source = audioContext.createBufferSource();
            source.buffer = audioBuffer;
            source.connect(audioContext.destination);
            source.onended = () => {
                playbackSourcesRef.current.delete(source);
            };

            const startAt = Math.max(audioContext.currentTime, playbackTimeRef.current);
            const wasAgentSpeaking = audioContext.currentTime < agentSpeakingUntilRef.current;
            playbackSourcesRef.current.add(source);
            source.start(startAt);
            if (!wasAgentSpeaking) {
                agentSpeakingStartedAtRef.current = audioContext.currentTime;
            }
            playbackTimeRef.current = startAt + audioBuffer.duration;
            agentSpeakingUntilRef.current = playbackTimeRef.current + 0.35;
        } catch (error) {
            console.error('[Voice] Playback error:', error);
            appendMessageToUI('system', 'Unable to play agent audio');
        }
    }, []);

    // Start chat session
    const sendChatStartEvent = () => {
        if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;

        console.log('[WS] Sending chat_start event');
        wsRef.current.send(JSON.stringify({
            event: 'chat_start',
            streamSid: streamSid.current,
            sessionId: sessionId.current,
            inputMode: inputModeRef.current,
            agentId: agentId,
            userId: userId
        }));
        chatStarted.current = true;
    };

    // Clear chat history
    const clearChatHistory = () => {
        localStorage.removeItem(STORAGE_KEY);
        setMessages([]);
        console.log('Chat history cleared');
    };

    // Close chat and disconnect WebSocket
    const closeChat = async () => {
        console.log('[WS] Closing chat connection');

        if (isVoiceChatActiveRef.current && wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify({ event: 'chat_end' }));
        }
        await stopAgentPlayback();
        await cleanupVoiceResources();

        if (wsRef.current) {
            wsRef.current.close();
            wsRef.current = null;
        }
        setIsConnected(false);
        chatStarted.current = false;

        window.parent.postMessage({
            type: 'close-widget'
        }, '*');
    };

    // Connect WebSocket
    const connectWebSocket = () => {
        if (isConnecting.current) {
            console.log('[WS] Already connecting, skipping...');
            return;
        }

        if (!wsUrl) {
            console.error('[WS] No WebSocket URL provided');
            setConnectionError('WebSocket URL not configured');
            return;
        }

        isConnecting.current = true;
        setConnectionError(null);

        console.log('[WS] Connecting to:', wsUrl);
        wsRef.current = new WebSocket(wsUrl);

        wsRef.current.onopen = () => {
            console.log('[WS] Connected to:', wsUrl);
            setIsConnected(true);
            setConnectionError(null);
            isConnecting.current = false;

            setTimeout(() => {
                if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
                    sendChatStartEvent();
                }
            }, 500);
        };

        wsRef.current.onmessage = (event) => {
            try {
                const data = JSON.parse(event.data);
                console.log('[WS] Received:', data);

                const eventType = data.event || data.type || data.sender;

                switch (eventType) {
                    case 'chat_started':
                        if (data.sessionId) {
                            sessionId.current = data.sessionId;
                        }
                        if (data.inputMode) {
                            inputModeRef.current = data.inputMode;
                        }
                        voiceSessionReadyRef.current = (data.inputMode || inputModeRef.current) === 'voice';
                        break;

                    case 'agent_message':
                        activeTranscriptMessageIdRef.current = null;
                        lastBotMessageRef.current = data.text || data.message || '';
                        appendMessageToUI('bot', data.text || data.message);
                        setTyping(false);
                        break;

                    case 'agent_audio':
                        if (data.audio && isVoiceChatActiveRef.current && Date.now() >= ignoreAgentAudioUntilRef.current) {
                            playAgentAudio(data.audio);
                        }
                        break;

                    case 'user_transcript':
                        upsertVoiceTranscript(
                            stripEchoedBotText(data.text, lastBotMessageRef.current),
                            data.isFinal || data.final || data.final_transcript
                        );
                        break;

                    case 'interruption':
                        stopAgentPlayback();
                        setTyping(false);
                        break;

                    case 'session_ended':
                        voiceSessionReadyRef.current = false;
                        stopAgentPlayback();
                        appendMessageToUI('system', 'Session ended');
                        setTyping(false);
                        break;

                    case 'bot':
                    case 'assistant':
                        appendMessageToUI('bot', data.message || data.text);
                        setTyping(false);
                        break;

                    case 'user':
                        appendMessageToUI('user', data.message || data.text);
                        break;

                    case 'system':
                        // Don't show "Session started" message
                        if (data.message && !data.message.toLowerCase().includes('session started')) {
                            appendMessageToUI('system', data.message || data.callSummary);
                        }
                        break;

                    case 'typing':
                        setTyping(data.isTyping);
                        break;

                    case 'error':
                        console.error('Server error:', data.message);
                        appendMessageToUI('system', `Error: ${data.message}`);
                        break;

                    default:
                        if (data.message) {
                            appendMessageToUI('bot', data.message);
                        } else if (data.text) {
                            appendMessageToUI('bot', data.text);
                        }
                        setTyping(false);
                        break;
                }
            } catch (error) {
                console.error('Error parsing message:', error);
            }
        };

        wsRef.current.onclose = () => {
            console.log('[WS] Disconnected from:', wsUrl);
            setIsConnected(false);
            chatStarted.current = false;
            isConnecting.current = false;
        };

        wsRef.current.onerror = (err) => {
            console.error('[WS] Error', err);
            setConnectionError('Connection error. Attempting to reconnect...');
            isConnecting.current = false;
        };
    };

    useEffect(() => {
        if (wsUrl && !wsRef.current && !isConnecting.current) {
            connectWebSocket();
        }

        return () => {
            if (wsRef.current) {
                wsRef.current.close();
                wsRef.current = null;
            }
            isConnecting.current = false;

            // Voice chat cleanup
            if (processorRef.current) {
                try {
                    processorRef.current.disconnect();
                } catch (e) {
                    // Ignore
                }
                processorRef.current = null;
            }
            if (mediaStreamSourceRef.current) {
                try {
                    mediaStreamSourceRef.current.disconnect();
                } catch (e) {
                    // Ignore
                }
                mediaStreamSourceRef.current = null;
            }
            if (streamRef.current) {
                streamRef.current.getTracks().forEach((track) => track.stop());
                streamRef.current = null;
            }
            if (audioContextRef.current) {
                try {
                    audioContextRef.current.close();
                } catch (e) {
                    // Ignore
                }
                audioContextRef.current = null;
            }
            if (playbackAudioContextRef.current) {
                try {
                    playbackAudioContextRef.current.close();
                } catch (e) {
                    // Ignore
                }
                playbackAudioContextRef.current = null;
            }
            if (recordingTimerRef.current) {
                clearInterval(recordingTimerRef.current);
            }
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
            }
        };
    }, [agentId, userId, wsUrl]);

    // Audio recording
    const startRecording = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            mediaRecorder.current = new MediaRecorder(stream);
            audioChunks.current = [];

            mediaRecorder.current.ondataavailable = e => audioChunks.current.push(e.data);
            mediaRecorder.current.onstop = async () => {
                const audioBlob = new Blob(audioChunks.current, { type: 'audio/webm' });
                await sendAudio(audioBlob);
                stream.getTracks().forEach(track => track.stop());
            };

            mediaRecorder.current.start();
            setIsRecording(true);
        } catch (error) {
            console.error('Error accessing microphone:', error);
            setConnectionError('Microphone access denied. Please check permissions.');
        }
    };

    const stopRecording = () => {
        if (mediaRecorder.current && isRecording) {
            mediaRecorder.current.stop();
            setIsRecording(false);
        }
    };

    // -----------------------------------------
    // LIVE VOICE CHAT (ported from ChatComponent.js)
    // -----------------------------------------
    const startAmplitudeVisualization = useCallback(() => {
        if (!audioContextRef.current || !analyserRef.current) return;

        const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);

        const updateAmplitude = () => {
            if (!isRecordingRef.current || !analyserRef.current) return;

            analyserRef.current.getByteTimeDomainData(dataArray);

            let maxSample = 0;
            for (let i = 0; i < dataArray.length; i++) {
                const normalized = (dataArray[i] - 128) / 128;
                maxSample = Math.max(maxSample, Math.abs(normalized));
            }

            setRecordingAmplitude(maxSample);
            animationFrameRef.current = requestAnimationFrame(updateAmplitude);
        };

        updateAmplitude();
    }, []);

    useEffect(() => {
        if (isRecording) {
            recordingTimerRef.current = setInterval(() => {
                if (recordingStartTimeRef.current) {
                    const elapsed = Math.floor(
                        (Date.now() - recordingStartTimeRef.current) / 1000
                    );
                    setRecordingDuration(elapsed);
                }
            }, RECORDING_TIMESLICE);

            startAmplitudeVisualization();
        } else {
            if (recordingTimerRef.current) {
                clearInterval(recordingTimerRef.current);
                recordingTimerRef.current = null;
            }
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
                animationFrameRef.current = null;
            }
            setRecordingDuration(0);
            setRecordingAmplitude(0);
        }

        return () => {
            if (recordingTimerRef.current) {
                clearInterval(recordingTimerRef.current);
                recordingTimerRef.current = null;
            }
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
                animationFrameRef.current = null;
            }
        };
    }, [isRecording, startAmplitudeVisualization]);

    const cleanupVoiceResources = useCallback(async () => {
        if (processorRef.current) {
            try {
                processorRef.current.disconnect();
            } catch (err) {
                console.warn('[Audio] Processor disconnect error:', err);
            }
            processorRef.current.onaudioprocess = null;
            processorRef.current = null;
        }

        if (mediaStreamSourceRef.current) {
            try {
                mediaStreamSourceRef.current.disconnect();
            } catch (err) {
                console.warn('[Audio] Source disconnect error:', err);
            }
            mediaStreamSourceRef.current = null;
        }

        if (streamRef.current) {
            streamRef.current.getTracks().forEach((track) => track.stop());
            streamRef.current = null;
        }

        if (audioContextRef.current) {
            try {
                await audioContextRef.current.close();
            } catch (err) {
                console.warn('[Audio] Context close error:', err);
            }
            audioContextRef.current = null;
        }

        analyserRef.current = null;
        isRecordingRef.current = false;
        isVoiceChatActiveRef.current = false;
        voiceSessionReadyRef.current = false;
        voiceChunkCountRef.current = 0;
        agentSpeakingStartedAtRef.current = 0;
        bargeInVoiceFramesRef.current = 0;
        lastBargeInAtRef.current = 0;
        ignoreAgentAudioUntilRef.current = 0;
        activeTranscriptMessageIdRef.current = null;
        recordingStartTimeRef.current = null;
        setIsRecording(false);
        setIsVoiceChatActive(false);
    }, []);

    const startVoiceChat = useCallback(async () => {
        if (!isConnected) {
            setConnectionError('Not connected to server');
            return;
        }

        if (isVoiceChatActiveRef.current) {
            return;
        }

        try {
            recordingStartTimeRef.current = Date.now();
            voiceSessionReadyRef.current = false;
            inputModeRef.current = 'voice';

            if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
                wsRef.current.send(JSON.stringify({ event: 'chat_end' }));
            }
            sessionId.current = generateSessionId();

            const stream = await navigator.mediaDevices.getUserMedia({
                audio: {
                    channelCount: 1,
                    echoCancellation: true,
                    noiseSuppression: true,
                    autoGainControl: true,
                },
            });
            streamRef.current = stream;

            if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
                wsRef.current.send(JSON.stringify({
                    event: 'chat_start',
                    streamSid: streamSid.current,
                    sessionId: sessionId.current,
                    inputMode: 'voice',
                    agentId,
                    userId,
                }));
            }

            const AudioContextClass = window.AudioContext || window.webkitAudioContext;
            audioContextRef.current = new AudioContextClass();
            if (audioContextRef.current.state === 'suspended') {
                await audioContextRef.current.resume();
            }
            analyserRef.current = audioContextRef.current.createAnalyser();
            analyserRef.current.fftSize = 2048;

            const source = audioContextRef.current.createMediaStreamSource(stream);
            const processor = audioContextRef.current.createScriptProcessor(VOICE_BUFFER_SIZE, 1, 1);
            mediaStreamSourceRef.current = source;
            processorRef.current = processor;

            processor.onaudioprocess = (event) => {
                if (
                    !isVoiceChatActiveRef.current ||
                    !voiceSessionReadyRef.current ||
                    !wsRef.current ||
                    wsRef.current.readyState !== WebSocket.OPEN
                ) {
                    return;
                }

                const inputData = event.inputBuffer.getChannelData(0);
                const isAgentSpeaking = playbackAudioContextRef.current &&
                    playbackAudioContextRef.current.currentTime < agentSpeakingUntilRef.current;

                if (isAgentSpeaking) {
                    if (
                        agentSpeakingStartedAtRef.current &&
                        playbackAudioContextRef.current.currentTime - agentSpeakingStartedAtRef.current <
                        BARGE_IN_PLAYBACK_GRACE_MS / 1000
                    ) {
                        return;
                    }

                    const rmsLevel = getRmsLevel(inputData);
                    const now = Date.now();
                    const canBargeIn = now - lastBargeInAtRef.current > BARGE_IN_COOLDOWN_MS;

                    if (rmsLevel >= BARGE_IN_RMS_THRESHOLD && canBargeIn) {
                        bargeInVoiceFramesRef.current += 1;
                    } else {
                        bargeInVoiceFramesRef.current = 0;
                    }

                    if (bargeInVoiceFramesRef.current < BARGE_IN_CONSECUTIVE_FRAMES) {
                        return;
                    }

                    lastBargeInAtRef.current = now;
                    ignoreAgentAudioUntilRef.current = now + 1200;
                    bargeInVoiceFramesRef.current = 0;
                    console.log('[Voice] Barge-in detected, stopping agent playback');
                    stopAgentPlayback();
                }

                const downsampled = downsampleBuffer(
                    inputData,
                    audioContextRef.current.sampleRate,
                    VOICE_SAMPLE_RATE
                );
                safeSendBinary(encodeUserAudio(downsampled));
                voiceChunkCountRef.current += 1;
            };

            source.connect(analyserRef.current);
            analyserRef.current.connect(processor);
            processor.connect(audioContextRef.current.destination);

            isRecordingRef.current = true;
            isVoiceChatActiveRef.current = true;
            setIsRecording(true);
            setIsVoiceChatActive(true);
            appendMessageToUI('system', 'Voice chat started');
            triggerHaptic(HAPTIC_START);
        } catch (error) {
            console.error('[Voice] Microphone error:', error);
            setConnectionError('Microphone access denied. Please check permissions.');
            await cleanupVoiceResources();
        }
    }, [agentId, userId, cleanupVoiceResources, isConnected, safeSendBinary, stopAgentPlayback]);

    const stopVoiceChat = useCallback(async () => {
        if (!isVoiceChatActiveRef.current) {
            return;
        }

        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify({ event: 'chat_end' }));
        }
        await stopAgentPlayback();
        await cleanupVoiceResources();
        inputModeRef.current = 'text';
        sessionId.current = generateSessionId();

        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify({
                event: 'chat_start',
                streamSid: streamSid.current,
                sessionId: sessionId.current,
                inputMode: 'text',
                agentId,
                userId,
            }));
        }
        appendMessageToUI('system', 'Voice chat ended');
        triggerHaptic(HAPTIC_STOP);
    }, [agentId, userId, cleanupVoiceResources, stopAgentPlayback]);

    const toggleVoiceChat = useCallback(() => {
        if (isVoiceChatActiveRef.current) {
            stopVoiceChat();
        } else {
            startVoiceChat();
        }
    }, [startVoiceChat, stopVoiceChat]);

    // Build dynamic styles from props.
    // CSS custom properties resolve inside inline style props, so this stays in
    // step with the brand token. The literal fallback matters here: this widget
    // is iframed onto customer sites where our stylesheet may not have loaded yet.
    const defaultAccentBg = 'var(--wr-gradient-brand, linear-gradient(92deg, #001FCC 0%, #0026FF 48%, #3355FF 100%))';

    const finalHeaderBg = headerColor || backgroundColor || defaultAccentBg;
    const finalButtonBg = buttonColor || defaultAccentBg;
    const finalUserMessageBg = headerColor || defaultAccentBg;
    const finalHeaderTextColor = textColor || 'white';
    const finalChatTextColor = chatTextColor || textColor || '#374151';

    const inlineStyles = {
        '--widget-bg': backgroundColor || 'white',
        '--widget-header-bg': finalHeaderBg,
        '--widget-header-text': finalHeaderTextColor,
        '--widget-chat-text': finalChatTextColor,
        '--widget-user-msg-bg': 'white',
        '--widget-user-msg-text': finalChatTextColor,
        '--widget-bot-msg-bg': 'white',
        '--widget-bot-msg-text': finalChatTextColor,
        '--widget-input-bg': backgroundColor || 'white',
        '--widget-input-border': '#e5e7eb',
        '--widget-input-text': finalChatTextColor,
        '--widget-send-btn-bg': finalButtonBg,
        '--widget-button-text': finalHeaderTextColor,
        '--widget-border-radius': borderRadius || '24px'
    };

    return (
        <div className="chat-widget-embed" style={inlineStyles}>
            {/* Header */}
            <div className="chat-header">
                <div className="header-content">
                    <div className="header-info">
                        <div className="avatar">
                            <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
                                <path d="M12 8V12L14 14" stroke="currentColor" strokeWidth="2" />
                            </svg>
                        </div>
                        <div>
                            <h3>AI Assistant</h3>
                            <div className="status">
                                <span className={`status-dot ${isConnected ? 'connected' : 'disconnected'}`}></span>
                                <span>{isConnected ? 'Online' : 'Connecting...'}</span>
                            </div>
                        </div>
                    </div>
                    <div className="header-actions">
                        {!isConnected && connectionError && (
                            <div className="error-tooltip" title={connectionError}>
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                    <path d="M12 8V12M12 16H12.01M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z" stroke="currentColor" strokeWidth="2" />
                                </svg>
                            </div>
                        )}
                        <button className="close-btn" onClick={closeChat} title="Close">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                            </svg>
                        </button>
                    </div>
                </div>
            </div>

            {/* Messages Container */}
            <div className="messages-container" ref={messagesContainerRef}>
                {messages.length === 0 && (
                    <div className="welcome-message">
                        <div className="welcome-icon">💬</div>
                        <h4>Welcome to AI Assistant</h4>
                        <p>Type a message or click the mic to talk</p>
                        <small>Click mic to start live voice chat</small>
                    </div>
                )}

                {messages.map((msg, idx) => (
                    <div key={idx} className={`message ${msg.sender}`}>
                        <div className="message-avatar">
                            {msg.sender === 'bot' ? '🤖' : msg.sender === 'user' ? '👤' : 'ℹ️'}
                        </div>
                        <div className="message-content">
                            <div className="message-text">{msg.text}</div>
                            <div className="message-time">{msg.timestamp}</div>
                        </div>
                    </div>
                ))}

                {typing && (
                    <div className="message bot">
                        <div className="message-avatar">🤖</div>
                        <div className="message-content">
                            <div className="typing-indicator">
                                <span></span>
                                <span></span>
                                <span></span>
                            </div>
                        </div>
                    </div>
                )}
                <div ref={messagesEndRef} />
            </div>

            {/* Input Area */}
            <div className="input-area">
                {isRecording && (
                    <div className="recording-overlay">
                        <div className="recording-panel">
                            <div className="recording-info">
                                <div className="recording-duration">
                                    <span className="recording-dot" />
                                    {formatDuration(recordingDuration)}
                                </div>
                                <div className="recording-instruction">
                                    Voice chat live
                                </div>
                            </div>
                            <div className="recording-waveform">
                                <div className="waveform-bars">
                                    {[...Array(AMPLITUDE_BARS_COUNT)].map((_, i) => (
                                        <div
                                            key={i}
                                            className="waveform-bar"
                                            style={{
                                                height: `${Math.max(
                                                    AMPLITUDE_MIN_HEIGHT,
                                                    Math.min(
                                                        AMPLITUDE_MAX_HEIGHT,
                                                        recordingAmplitude *
                                                        AMPLITUDE_SCALE *
                                                        (0.5 + Math.sin((i * Math.PI) / 10) * 0.5)
                                                    )
                                                )}px`,
                                            }}
                                        />
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
                <div className="input-container">
                    <input
                        type="text"
                        value={input}
                        onChange={e => setInput(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && sendMessage(input)}
                        placeholder="Type a message..."
                        className="message-input"
                        disabled={!isConnected || isVoiceChatActive}
                    />
                    <div className="input-actions">
                        {/* <button
                            className={`voice-btn ${isRecording ? 'recording' : ''}`}
                            onMouseDown={startRecording}
                            onMouseUp={stopRecording}
                            onTouchStart={startRecording}
                            onTouchEnd={stopRecording}
                            disabled={!isConnected}
                        >
                            {isRecording ? (
                                <div className="recording-wave">
                                    <span></span>
                                    <span></span>
                                    <span></span>
                                </div>
                            ) : (
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                    <path d="M12 15C13.66 15 15 13.66 15 12V6C15 4.34 13.66 3 12 3C10.34 3 9 4.34 9 6V12C9 13.66 10.34 15 12 15Z" stroke="currentColor" strokeWidth="2" />
                                    <path d="M19 12C19 15.86 15.86 19 12 19C8.14 19 5 15.86 5 12" stroke="currentColor" strokeWidth="2" />
                                    <path d="M12 22V19" stroke="currentColor" strokeWidth="2" />
                                </svg>
                            )}
                        </button> */}
                        <button
                            className={`voice-btn ${isVoiceChatActive ? 'recording-active' : ''}`}
                            onClick={toggleVoiceChat}
                            disabled={!isConnected}
                            title={isVoiceChatActive ? 'End voice chat' : 'Start voice chat'}
                        >
                            {isVoiceChatActive ? (
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                    <path d="M6 6H18V18H6V6Z" fill="currentColor" />
                                </svg>
                            ) : (
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                    <path d="M12 15C13.66 15 15 13.66 15 12V6C15 4.34 13.66 3 12 3C10.34 3 9 4.34 9 6V12C9 13.66 10.34 15 12 15Z" stroke="currentColor" strokeWidth="2" />
                                    <path d="M19 12C19 15.86 15.86 19 12 19C8.14 19 5 15.86 5 12" stroke="currentColor" strokeWidth="2" />
                                    <path d="M12 22V19" stroke="currentColor" strokeWidth="2" />
                                </svg>
                            )}
                        </button>
                        <button
                            className="send-btn"
                            onClick={() => sendMessage(input)}
                            disabled={!input.trim() || !isConnected || isVoiceChatActive}
                        >
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                <path d="M22 2L11 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                                <path d="M22 2L15 22L11 13L2 9L22 2Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
                            </svg>
                        </button>
                    </div>
                </div>
                {!isConnected && connectionError && (
                    <div className="connection-error">
                        {connectionError}
                    </div>
                )}
            </div>
        </div>
    );
}